/**
 * Hair front/back sheets. The identity-carrier: strongest silhouette on the
 * sprite. Authored in HAIR_AUTHOR indices; crown light wedge top-left, dark
 * fringe/underside rows. No contour here — compose.js owns it.
 */
import { HAIR_AUTHOR } from '../catalog.js';
import {
  canvas,
  fillRect,
  hLine,
  mirrorFrame,
  put,
  vLine,
  walkSheet,
} from '../../pixel-kit.js';
import { poseFor } from './rig.js';

const [HAIR_DK, HAIR_MD, HAIR_LT] = HAIR_AUTHOR;

function blank() {
  return canvas(16, 24);
}

/** Cap over the skull: y=1..5 with 1px overhang past the head sides. */
function cap(px, bob) {
  fillRect(px, 5, 1 + bob, 6, 1, HAIR_MD);
  fillRect(px, 4, 2 + bob, 8, 1, HAIR_MD);
  fillRect(px, 3, 3 + bob, 10, 2, HAIR_MD);
  hLine(px, 4, 11, 5 + bob, HAIR_DK); // fringe shadow over the brow
  // crown light wedge
  hLine(px, 5, 8, 1 + bob, HAIR_LT);
  hLine(px, 4, 6, 2 + bob, HAIR_LT);
}

function frontDown(style, frame) {
  const px = blank();
  const { bob } = poseFor('down', frame);
  cap(px, bob);
  // sideburn points
  put(px, 3, 5 + bob, HAIR_MD);
  put(px, 12, 5 + bob, HAIR_MD);
  if (style === 'medium') {
    vLine(px, 3, 5 + bob, 8 + bob, HAIR_MD);
    vLine(px, 12, 5 + bob, 8 + bob, HAIR_MD);
    put(px, 3, 8 + bob, HAIR_DK);
    put(px, 12, 8 + bob, HAIR_DK);
  } else if (style === 'long') {
    fillRect(px, 3, 5 + bob, 1, 6, HAIR_MD);
    fillRect(px, 12, 5 + bob, 1, 6, HAIR_MD);
    put(px, 3, 10 + bob, HAIR_DK);
    put(px, 12, 10 + bob, HAIR_DK);
  } else {
    put(px, 3, 6 + bob, HAIR_MD);
    put(px, 12, 6 + bob, HAIR_MD);
  }
  return px;
}

function frontUp(style, frame) {
  const px = blank();
  const { bob } = poseFor('up', frame);
  // full back of head, no face
  fillRect(px, 5, 1 + bob, 6, 1, HAIR_MD);
  fillRect(px, 4, 2 + bob, 8, 2, HAIR_MD);
  fillRect(px, 3, 4 + bob, 10, 3, HAIR_MD);
  fillRect(px, 4, 7 + bob, 8, 1, HAIR_MD);
  hLine(px, 4, 11, 8 + bob, HAIR_DK); // nape shadow
  hLine(px, 5, 8, 1 + bob, HAIR_LT);
  hLine(px, 4, 6, 2 + bob, HAIR_LT);
  if (style === 'medium') {
    fillRect(px, 4, 8 + bob, 8, 2, HAIR_MD);
    hLine(px, 4, 11, 9 + bob, HAIR_DK);
  }
  return px;
}

function frontSide(style, frame) {
  const px = blank();
  const { bob } = poseFor('left', frame);
  // facing left: fringe at low x, mass at the back (high x)
  fillRect(px, 5, 1 + bob, 6, 1, HAIR_MD);
  fillRect(px, 4, 2 + bob, 8, 1, HAIR_MD);
  fillRect(px, 3, 3 + bob, 10, 2, HAIR_MD);
  hLine(px, 3, 5, 5 + bob, HAIR_DK); // brow fringe
  put(px, 3, 5 + bob, HAIR_MD);
  // back of the skull stays covered down to the nape
  fillRect(px, 10, 5 + bob, 3, 2, HAIR_MD);
  fillRect(px, 10, 7 + bob, 2, 2, HAIR_MD);
  put(px, 12, 6 + bob, HAIR_DK);
  put(px, 11, 8 + bob, HAIR_DK);
  hLine(px, 5, 8, 1 + bob, HAIR_LT);
  hLine(px, 4, 6, 2 + bob, HAIR_LT);
  if (style === 'medium') {
    fillRect(px, 10, 7 + bob, 3, 2, HAIR_MD);
    put(px, 12, 8 + bob, HAIR_DK);
  }
  return px;
}

function drawFront(style, direction, frame) {
  if (direction === 'left') return frontSide(style, frame);
  if (direction === 'right') return mirrorFrame(frontSide(style, frame));
  if (direction === 'up') return frontUp(style, frame);
  return frontDown(style, frame);
}

function backDown(frame) {
  const px = blank();
  const { bob, swing } = poseFor('down', frame);
  // falling mass behind the torso, peeking past the shoulders
  fillRect(px, 3, 6 + bob, 2, 8, HAIR_MD);
  fillRect(px, 11, 6 + bob, 2, 8, HAIR_MD);
  put(px, 3 + swing, 14 + bob, HAIR_DK);
  put(px, 12 + swing, 14 + bob, HAIR_DK);
  return px;
}

function backUp(frame) {
  const px = blank();
  const { bob, swing } = poseFor('up', frame);
  fillRect(px, 4, 8 + bob, 8, 6, HAIR_MD);
  fillRect(px, 4 + swing, 14 + bob, 8, 1, HAIR_MD);
  hLine(px, 4 + swing, 11 + swing, 15 + bob, HAIR_DK);
  hLine(px, 5, 7, 9 + bob, HAIR_LT);
  return px;
}

function backSide(frame) {
  const px = blank();
  const { bob, swing } = poseFor('left', frame);
  // facing left: straight fall behind the skull, tip sways with the stride
  fillRect(px, 10, 6 + bob, 3, 6, HAIR_MD);
  fillRect(px, 10 + swing, 12 + bob, 3, 2, HAIR_MD);
  hLine(px, 10 + swing, 12 + swing, 13 + bob, HAIR_DK);
  return px;
}

function drawBack(style, direction, frame) {
  if (style !== 'long') return blank();
  if (direction === 'left') return backSide(frame);
  if (direction === 'right') return mirrorFrame(backSide(frame));
  if (direction === 'up') return backUp(frame);
  return backDown(frame);
}

export function hairFrontSheet(style) {
  return walkSheet((direction, frame) => drawFront(style, direction, frame));
}

export function hairBackSheet(style) {
  return walkSheet((direction, frame) => drawBack(style, direction, frame));
}
